import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Ppp } from "@/types";
import { ApiError } from "@/lib/apiClient";
import { getPppConfidentialMessage } from "@/lib/ppp-errors";
import { useCreatePpp, useUpdatePpp } from "@/hooks/usePppMutations";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { UserCombobox } from "@/components/UserCombobox";
import { KeyResultMultiSelect } from "./KeyResultMultiSelect";
import { PppConfidentialFields } from "./PppConfidentialFields";
import { Loader2 } from "lucide-react";
import { pppSchema, PppFormValues } from "./ppp-form-schema";
import { PPP_CATEGORIES, PPP_CATEGORY_LABEL, PPP_TYPE_HINT, PPP_TYPE_LABEL, PPP_TYPES } from "./ppp-constants";

interface PppFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → create */
  ppp: Ppp | null;
}

const NONE = "none";

export function PppFormDialog({ open, onOpenChange, ppp }: PppFormDialogProps) {
  const create = useCreatePpp();
  const update = useUpdatePpp();
  const isEditing = !!ppp;

  const form = useForm<PppFormValues>({
    resolver: zodResolver(pppSchema),
    defaultValues: {
      name: ppp?.name ?? "",
      description: ppp?.description ?? "",
      type: ppp?.type,
      category: ppp?.category ?? "",
      keyResultPids: ppp?.keyResults.map((kr) => kr.pid) ?? [],
      driUserPid: ppp?.dri?.pid ?? "",
      isConfidential: ppp?.isConfidential ?? false,
      memberPids: ppp?.members.map((m) => m.pid) ?? [],
    },
  });
  const type = form.watch("type");
  const isSubmitting = create.isPending || update.isPending;

  const onSubmit = async (values: PppFormValues) => {
    const payload = {
      name: values.name,
      // On edit, an emptied field must clear the stored text; `undefined` would leave it untouched.
      description: values.description || (ppp ? null : undefined),
      type: values.type,
      category: values.category || null,
      keyResultPids: values.keyResultPids,
      driUserPid: values.driUserPid || null,
      isConfidential: !!values.isConfidential,
      memberPids: values.memberPids,
    };
    try {
      if (ppp) {
        await update.mutateAsync({ pid: ppp.pid, data: payload });
        toast.success("PPP updated");
      } else {
        await create.mutateAsync(payload);
        toast.success("PPP created", { description: `"${values.name}" is now at the bottom of the ranking.` });
      }
      onOpenChange(false);
    } catch (e) {
      const msg = getPppConfidentialMessage(e)
        ?? (e instanceof ApiError && typeof e.body.message === "string" ? e.body.message : "Please try again.");
      toast.error(isEditing ? "Could not update PPP" : "Could not create PPP", { description: msg });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit PPP" : "New PPP"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form id="ppp-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl><Input placeholder="e.g. Infina App, ZaloPay, Software Factory" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="type" render={({ field }) => (
                <FormItem>
                  <FormLabel>Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl><SelectTrigger><SelectValue placeholder="Product / Partner / Project" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {PPP_TYPES.map((t) => <SelectItem key={t} value={t}>{PPP_TYPE_LABEL[t]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {type && <FormDescription className="text-xs">{PPP_TYPE_HINT[type]}</FormDescription>}
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="category" render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select onValueChange={(v) => field.onChange(v === NONE ? "" : v)} value={field.value || NONE}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {PPP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{PPP_CATEGORY_LABEL[c]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="keyResultPids" render={({ field }) => (
              <FormItem>
                <FormLabel>Key Results *</FormLabel>
                <KeyResultMultiSelect value={field.value} onChange={field.onChange} disabled={isSubmitting} />
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="driUserPid" render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>DRI</FormLabel>
                <FormControl>
                  <UserCombobox value={field.value} onChange={field.onChange} initialUser={ppp?.dri ?? null} placeholder="Select DRI…" clearable />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl><Textarea rows={3} placeholder="Optional context…" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <PppConfidentialFields form={form} disabled={isSubmitting} initialMembers={ppp?.members} />
          </form>
        </Form>
        <DialogFooter>
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form="ppp-form" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Save Changes" : "Create PPP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
